import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, LoginChallengeResponse } from '../types';
import { authService } from '../services/auth.service';
import { policiesService } from '../services/policies.service';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasAcceptedPolicies: boolean;
  checkPolicies: () => Promise<boolean>;
  login: (empNo: string, password: string) => Promise<LoginChallengeResponse>;
  completeLogin: (verificationId: string, code: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(true);

  const isPublicAuthRoute = () => {
    const path = window.location.pathname;
    return path === '/login' || path === '/signup';
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (isPublicAuthRoute()) {
        authService.clearUser();
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        // The HttpOnly cookie is sent automatically by the browser.
        // If it's valid, the backend returns the user profile; if not, it throws 401.
        // The 401 interceptor in api.ts does NOT redirect when already on /login,
        // so this is safe to call on every page including the login page itself.
        const profile = await authService.getProfile();
        authService.setUser(profile);
        setUser(profile);
        await checkPoliciesStatus();
      } catch {
        // No valid session — clear any stale cached user and stay on current page.
        authService.clearUser();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPoliciesStatus = async () => {
    try {
      const status = await policiesService.getUserStatus();
      setHasAcceptedPolicies(status.hasAcceptedAll);
    } catch {
      setHasAcceptedPolicies(true);
    }
  };

  const checkPolicies = async (): Promise<boolean> => {
    try {
      const status = await policiesService.getUserStatus();
      setHasAcceptedPolicies(status.hasAcceptedAll);
      return status.hasAcceptedAll;
    } catch {
      return true;
    }
  };

  const login = async (empNo: string, password: string): Promise<LoginChallengeResponse> => {
    const credentials: LoginRequest = { empNo, password };
    return authService.login(credentials);
  };

  const completeLogin = async (verificationId: string, code: string) => {
    // verifyLogin returns { user } — the JWT is set as an HttpOnly cookie by the backend.
    const { user: loggedInUser } = await authService.verifyLogin(verificationId, code);
    authService.setUser(loggedInUser);
    setUser(loggedInUser);
    await checkPoliciesStatus();
  };

  const logout = () => {
    setUser(null);
    // Fire-and-forget: clears the HttpOnly cookie on the server, then redirects.
    void authService.logout();
  };

  const updateUser = (updatedUser: Partial<User>) => {
    if (user) {
      const mergedUser = { ...user, ...updatedUser };
      setUser(mergedUser);
      authService.setUser(mergedUser);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        hasAcceptedPolicies,
        checkPolicies,
        login,
        completeLogin,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
